import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Menu, Plus, Search, Trash2, Phone, Mail, UserCheck, Lock, Eye, EyeOff, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";

const API = "/api";

const StaffManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [createWithPassword, setCreateWithPassword] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`, { withCredentials: true });
      setStaff(response.data);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password if creating with password
    if (createWithPassword && formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      // First add to staff collection
      await axios.post(`${API}/staff`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      }, { withCredentials: true });

      // If password provided, also create user account with staff role
      if (createWithPassword && formData.password) {
        try {
          await axios.post(`${API}/staff/create-account`, {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            password: formData.password,
          }, { withCredentials: true });
          toast.success(`Staff member added! They can login with email: ${formData.email}`);
        } catch (accountError) {
          // Staff added but account creation failed
          console.error("Account creation failed:", accountError);
          toast.success("Staff member added. Account creation failed - they can still use Google login.");
        }
      } else {
        toast.success("Staff member added. They can login with Google or Apple.");
      }

      setDialogOpen(false);
      setFormData({ name: "", email: "", phone: "", password: "" });
      setCreateWithPassword(true);
      fetchStaff();
    } catch (error) {
      console.error("Failed to add staff:", error);
      toast.error(error.response?.data?.detail || "Failed to add staff");
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm("Are you sure you want to remove this staff member?")) return;
    try {
      await axios.delete(`${API}/staff/${staffId}`, { withCredentials: true });
      toast.success("Staff member removed");
      fetchStaff();
    } catch (error) {
      console.error("Failed to delete staff:", error);
      toast.error("Failed to remove staff");
    }
  };

  const filteredStaff = staff.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase">Staff Management</h1>
                <p className="text-[#A1A1AA] text-sm">{staff.length} staff members</p>
              </div>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="add-staff-btn"
                  className="bg-[#39FF14] text-black hover:bg-[#32D711] hover:shadow-[0_0_15px_rgba(57,255,20,0.6)] transition-all font-bold rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Staff
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl uppercase">Add Staff Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-[#A1A1AA]">Name</Label>
                    <Input
                      data-testid="staff-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                      <Input
                        data-testid="staff-email-input"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                        placeholder="staff@example.com"
                        required
                      />
                    </div>
                    <p className="text-[#A1A1AA] text-xs mt-1">
                      Any email works: Gmail, Yahoo, iCloud, Outlook, etc.
                    </p>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Phone <span className="text-[#A1A1AA]/50">(optional)</span></Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                      <Input
                        data-testid="staff-phone-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-10 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                        placeholder="+81 90-1234-5678"
                      />
                    </div>
                    <p className="text-[#A1A1AA]/60 text-xs mt-1">Leave blank if not available</p>
                  </div>

                  {/* Password Toggle */}
                  <div className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg">
                    <div>
                      <p className="text-white text-sm font-medium">Create login password</p>
                      <p className="text-[#A1A1AA] text-xs">Allow staff to login with email & password</p>
                    </div>
                    <Switch
                      checked={createWithPassword}
                      onCheckedChange={setCreateWithPassword}
                      className="data-[state=checked]:bg-[#39FF14]"
                    />
                  </div>

                  {createWithPassword && (
                    <div className="animate-fade-in">
                      <Label className="text-[#A1A1AA]">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                        <Input
                          data-testid="staff-password-input"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="pl-10 pr-10 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                          placeholder="Min. 6 characters"
                          required={createWithPassword}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-[#A1A1AA] text-xs mt-1">
                        Share this password with your staff member
                      </p>
                    </div>
                  )}

                  <Button 
                    type="submit"
                    data-testid="save-staff-btn"
                    className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                  >
                    Add Staff Member
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
            <Input
              data-testid="search-staff-input"
              placeholder="Search staff..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14] max-w-md"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">No staff members</h3>
              <p className="text-[#A1A1AA] mb-4">
                {searchQuery ? "Try a different search term" : "Add your first staff member to get started"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredStaff.map((member) => (
                <div
                  key={member.staff_id}
                  data-testid={`staff-card-${member.staff_id}`}
                  className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#39FF14]/50 transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#064E3B] rounded-full flex items-center justify-center">
                        <span className="text-[#39FF14] font-bold text-lg">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{member.name}</h3>
                        <div className="flex items-center gap-1 text-[#39FF14] text-xs">
                          <UserCheck className="w-3 h-3" />
                          <span>Staff</span>
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={`delete-staff-${member.staff_id}`}
                      onClick={() => handleDelete(member.staff_id)}
                      className="p-2 text-[#A1A1AA] hover:text-red-400 hover:bg-[#27272A] rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-[#A1A1AA]">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{member.email}</span>
                    </div>
                    {member.phone && (
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Phone className="w-4 h-4" />
                        <span>{member.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info Box */}
          <div className="mt-8 p-4 bg-[#064E3B]/20 border border-[#064E3B]/50 rounded-xl">
            <h3 className="text-white font-semibold mb-2">How Staff Login Works</h3>
            <ul className="text-[#A1A1AA] text-sm space-y-1">
              <li>• Add staff with any email (Gmail, Yahoo, iCloud, Outlook, etc.)</li>
              <li>• Create a password for them to login with email</li>
              <li>• Or they can use Google/Apple sign-in if their email matches</li>
              <li>• Staff go to /staff-login → Staff tab → Sign in</li>
              <li>• They'll only see jobs assigned to them</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StaffManagement;
